import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
} from "react-virtualized";
import { classes } from "portal/theme/theme";
import { Divider, LinearProgress, Typography } from "@mui/material";
import { jobExplorer, JobSelectionType } from "common/state/jobExplorer";
import { Objective } from "protos/rtc/jobs";
import { SelectionButton } from "portal/components/map/navigation/SelectionButton";
import { useDispatch, useSelector } from "react-redux";
import DoneIcon from "@mui/icons-material/Done";
import React, { CSSProperties, FC, ReactNode, useRef, useState } from "react";
interface ObjectivesSelectionListProps {
  objectives: Objective[];
}
// use this if you have more than a handful of objectives. Virtualizes the list so you can show many many objectives
export const ObjectivesSelectionList: FC<ObjectivesSelectionListProps> = ({
  objectives,
}: ObjectivesSelectionListProps) => {
  const { setFocus, setSelection } = jobExplorer.actions;
  const dispatch = useDispatch();
  const selection = useSelector(jobExplorer.selectors.selectSelection);
  const focus = useSelector(jobExplorer.selectors.selectFocus);

  const isObjectiveFocused = (id: number): boolean => {
    return (
      focus?.type === JobSelectionType.OBJECTIVE && focus.selection.id === id
    );
  };
  const isObjectiveSelected = (id: number): boolean => {
    return (
      selection?.type === JobSelectionType.OBJECTIVE &&
      selection.selection.id === id
    );
  };

  const cacheRef = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 60,
    })
  );
  const cache = cacheRef.current;

  const renderRow = ({
    style,
    index,
    key,
    parent,
  }: {
    style: CSSProperties;
    index: number;
    key: string;
    parent: any;
  }): ReactNode => {
    const objective = objectives[index];
    return (
      <CellMeasurer cache={cache} key={key} parent={parent} rowIndex={index}>
        {({ registerChild }) => (
          <div ref={registerChild as any} style={style}>
            {objective && (
              <button
                key={objective.id}
                className={classes(
                  "bg-transparent border-none text-inherit w-full",
                  "flex flex-col cursor-pointer",
                  isObjectiveFocused(objective.id) && "bg-blue-400",
                  isObjectiveSelected(objective.id) && "bg-blue-800"
                )}
                onMouseEnter={() =>
                  dispatch(
                    setFocus({
                      type: JobSelectionType.OBJECTIVE,
                      selection: objective,
                    })
                  )
                }
                onMouseLeave={() => dispatch(setFocus(undefined))}
                onClick={() =>
                  dispatch(
                    setSelection({
                      type: JobSelectionType.OBJECTIVE,
                      selection: objective,
                    })
                  )
                }
              >
                <div>
                  <SelectionButton
                    subSection
                    onClick={() =>
                      dispatch(
                        setSelection({
                          type: JobSelectionType.OBJECTIVE,
                          selection: objective,
                        })
                      )
                    }
                    isSelected={isObjectiveSelected(objective.id)}
                    isFocused={isObjectiveFocused(objective.id)}
                    endIcon={
                      objective.progressPercent === 100 && (
                        <DoneIcon className="fill-green" />
                      )
                    }
                  >
                    <Typography className="text-sm">
                      {objective.name}
                    </Typography>
                  </SelectionButton>
                </div>
                <div className="flex items-center gap-2 px-4">
                  <div className="w-full">
                    <LinearProgress
                      variant="determinate"
                      color="success"
                      value={objective.progressPercent}
                    />
                  </div>
                  <Typography>{`${objective.progressPercent}%`}</Typography>
                </div>
                <Divider />
              </button>
            )}
          </div>
        )}
      </CellMeasurer>
    );
  };

  const listRef = useRef<List>(null);
  const [prevWidth, setPrevWidth] = useState<number>(0);

  return (
    <AutoSizer className="min-h-96">
      {({ height, width }) => {
        if (prevWidth !== width) {
          cache.clearAll();
          listRef.current?.recomputeRowHeights();
        }
        setPrevWidth(width);
        return (
          <List
            ref={listRef}
            height={height}
            width={width}
            deferredMeasurementCache={cache}
            rowHeight={cache.rowHeight}
            rowCount={objectives.length}
            rowRenderer={renderRow}
          />
        );
      }}
    </AutoSizer>
  );
};
